<template>
  <div
    class="members-model-wrap electron-no-drag group"
    :class="{ 'card-sidebar-members-wrap': isCardSidebar }"
  >
    <div
      v-if="membersCount > 0"
      class="list-user-avatar-sections"
      @click="clickHandler"
      @mousedown.prevent.stop
    >
      <div class="members">
        <div
          v-if="!isCardPreview"
          class="avatars-draggable"
        >
          <UserAvatar
            v-for="member in firstFiveMembers"
            :key="member.user_id"
            class="member-avatar"
            :member="member"
            :size="avatarSize"
            :data-id="member.user_id"
            :group-avatar-id="groupAvatarId"
          />
          <span
            v-if="showOnlyAssignee"
            v-member-hover-preview="getMemberHover"
            :class="ownerModel ? 'only-owner' : 'only-assignee'"
          >
            {{ getFirstMemberLabel }}
          </span>
          <div
            v-if="restMembersCount > 0"
            v-tooltip="{
              content: translate('editAssignees'),
              onShow: () => disableEditMembersTooltip && !isGuestReader,
              placement: 'bottom',
              delay: { show: 300, hide: 100 },
            }"
            class="member-avatar rest-member-wrap pt"
          >
            <div class="rest-members-count">+{{ restMembersCount }}</div>
          </div>
          <span
            v-if="showPlaceholder"
            class="card-sidebar-label"
          >
            {{ modelLabel }}
          </span>
          <div
            v-if="ownerModel && membersCount"
            class="action-button-group-item ml-auto group-hover:flex group-hover:visible"
            :class="{ hidden: !isMobile }"
            @touchstart.prevent="removeOwner"
            @click.prevent="removeOwner"
            @mouseup.prevent.stop
          >
            <em
              class="custom-icons"
              :style="getCustomIconStyle(iconPath('x'))"
            />
          </div>
        </div>
        <div
          v-if="isCardPreview"
          class="avatars-draggable"
        >
          <UserAvatar
            v-for="member in cardPreviewMembers"
            :key="member.user_id"
            class="member-avatar"
            :member="member"
            :size="avatarSize"
            :data-id="member.user_id"
            :group-avatar-id="groupAvatarId"
          />
          <div
            v-if="restMembersCount > 0"
            v-tooltip="{
              content: translate('editAssignees'),
              onShow: () => disableMembersTooltip,
              placement: 'bottom',
              delay: { show: 300, hide: 100 },
            }"
            class="member-avatar rest-member-wrap pt"
          >
            <div class="rest-members-count">+{{ restMembersCount }}</div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!membersCount && !areProjectMembers"
      v-tooltip="{
        content: translate(ownerModel ? 'addOwner' : 'editAssignees'),
        onShow: () => disableMembersTooltip && !isGuestReader,
        placement: 'bottom',
        delay: { show: 300, hide: 100 },
      }"
      class="add-members-wrap pt"
      @click="handleAddMembersWrapClick"
    >
      <div
        class="add-members-button"
        @mousedown.stop
      >
        <em
          :style="getCustomIconStyle(iconPath('user-plus'))"
          :class="[newMemberIconClass, { 'card-sidebar-icon': isCardSidebar }]"
        />
      </div>
      <span
        v-if="showPlaceholder"
        class="card-sidebar-label"
      >
        {{ modelLabel }}
      </span>
    </div>
  </div>
</template>

<script>
import { nanoid } from 'nanoid';
import EventBus from '@/utilities/eventBus';
import customIconsMixin from '@/mixins/customIconsMixin';
import getMemberLabel from '@/utilities/getMemberLabel';
import UserAvatar from './UserAvatar';
import useCurrentRole from '@/utilities/composables/useCurrentRole';

export default {
  name: 'CurrentModelMembers',
  components: {
    UserAvatar,
  },
  mixins: [customIconsMixin],
  props: {
    members: {
      type: Array,
      required: false,
      default: () => [],
    },
    isCardPreview: {
      type: Boolean,
      required: false,
      default: false,
    },
    areProjectMembers: {
      type: Boolean,
      required: false,
      default: false,
    },
    showPlaceholder: {
      type: Boolean,
      required: false,
      default: false,
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    onlyAssignee: {
      type: Boolean,
      required: false,
      default: false,
    },
    ownerModel: {
      type: Boolean,
      required: false,
      default: false,
    },
    isTemplate: {
      type: Boolean,
      required: false,
      default: false,
    },
    isComment: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPageInfoSidebar: {
      type: Boolean,
      required: false,
      default: false,
    },
    isCardSidebar: {
      type: Boolean,
      required: false,
      default: false,
    },
    avatarSize: {
      type: String,
      required: false,
      default: 'small',
    },

    newMemberIconClass: {
      type: String,
      required: false,
      default: 'custom-icons',
    },
  },
  emits: ['removeOwner', 'click'],

  setup() {
    const { isGuestReader } = useCurrentRole();

    return {
      isGuestReader,
    };
  },

  data() {
    return {
      groupAvatarId: nanoid(6),
    };
  },
  computed: {
    modelLabel() {
      return this.ownerModel ? this.translate('owner') : this.assigneeLabel;
    },
    assigneeLabel() {
      switch (this.membersCount) {
        case 0:
          return this.translate('assign');
        case 1:
          return this.translate('assigned');
        default:
          return `${this.membersCount} ${this.translate('assignees').toLowerCase()}`;
      }
    },
    firstFiveMembers() {
      return (this.members || []).slice(
        0,
        this.isMobile
          ? this.$constants.smallCardMemberCountMax
          : this.$constants.openedCardMemberCountMax
      );
    },
    cardPreviewMembers() {
      const { smallCardMemberCountMax } = this.$constants;
      if (!this.members || !this.members.length) return [];
      if (this.members.length <= smallCardMemberCountMax) {
        return this.members.slice(0, smallCardMemberCountMax);
      }
      return (this.members || []).slice(0, smallCardMemberCountMax - 1);
    },
    membersCount() {
      return (this.members || []).length;
    },
    restMembersCount() {
      const { smallCardMemberCountMax, openedCardMemberCountMax } = this.$constants;
      const numberOfMembers = this.isMobile ? smallCardMemberCountMax : openedCardMemberCountMax;
      const membersCount = isNaN(this.membersCount) ? 0 : this.membersCount;
      const smallMemberCount =
        membersCount <= smallCardMemberCountMax
          ? smallCardMemberCountMax
          : smallCardMemberCountMax - 1;

      return membersCount - (this.isCardPreview ? smallMemberCount : numberOfMembers);
    },
    showOnlyAssignee() {
      return this.membersCount === 1 && this.onlyAssignee;
    },

    membersWrapTooltipClasses() {
      return this.disabled || this.isMobile || this.areProjectMembers
        ? 'hide-tooltip'
        : 'black-tooltip card-preview-tooltip pen';
    },
    disableMembersTooltip() {
      return !(this.disabled || this.isMobile || this.areProjectMembers);
    },
    disableEditMembersTooltip() {
      return !(
        this.disabled ||
        this.isMobile ||
        this.areProjectMembers ||
        this.isComment ||
        this.isPageInfoSidebar
      );
    },
    getFirstMember() {
      if (!this.members || !this.members[0]) return '';
      const { user_id } = this.members[0];
      return this.$store.getters.getMemberById(user_id);
    },
    getMemberHover() {
      if (this.isGuestRole && Object.keys(this.getFirstMember).length === 0) {
        return { id: this.members?.[0]?.user_id };
      }
      return this.getFirstMember;
    },
    getFirstMemberLabel() {
      if (this.isGuestRole && Object.keys(this.getFirstMember).length === 0) {
        return this.translate('hiddenMember');
      }
      return getMemberLabel(this.getFirstMember);
    },

    quickOrChildCardCreation() {
      return this.$store.getters.getQuickCardIsOn || this.$store.getters.getChildCardCreation;
    },
  },
  methods: {
    removeOwner($evt) {
      this.$emit('removeOwner', $evt);
    },

    clickHandler(evt) {
      if (this.quickOrChildCardCreation || this.isTemplate || this.isCardSidebar) {
        this.$emit('click');
        return;
      }
      document.body.click();
      if (this.disabled) {
        evt.stopPropagation();
        evt.preventDefault();
        return;
      }

      EventBus.$emit('membersDropdownClick');
    },

    handleAddMembersWrapClick(evt) {
      if (this.disabled) {
        evt.preventDefault();
        evt.stopPropagation();
      }
    },
  },
};
</script>
<style lang="scss">
.members-model-wrap {
  margin-right: 3px;
  &:hover:not(.mobile *) {
    .remove-owner-icon {
      display: flex;
    }

    .add-members-wrap {
      em {
        @apply bg-icon-hover;
      }
    }
  }

  &:hover:not(.mobile *):not(.card-sidebar-members-wrap) {
    .add-members-wrap {
      .add-members-button {
        @apply bg-icon-bg;
      }
    }
  }

  &.card-sidebar-members-wrap {
    .list-user-avatar-sections {
      .only-assignee,
      .only-owner {
        @apply ml-2;
      }
      .member-avatar {
        &:first-of-type {
          @apply ml-0;
        }
      }
    }
    .add-members-button {
      @apply m-0 p-0 rounded-none;
      border: unset;
    }

    .members {
      .rest-member-wrap {
        width: 18px;
        height: 18px;
      }
    }
  }
}
.list-user-avatar-sections {
  .only-assignee,
  .only-owner {
    @apply truncate text-sm text-text-default;
    margin-left: 6px;
  }
  .remove-owner-icon {
    @apply overflow-hidden cursor-pointer ml-auto hidden shrink-0 text-icon-default hover:text-icon-hover;
    font-size: 16px;
    max-width: 16px;
  }
  .members {
    @apply flex items-center;

    .avatars-draggable {
      @apply flex items-center;
    }

    .member-avatar {
      border-radius: 50%;
      &:not(.hidden-avatar) {
        border: 2px solid;
        @apply border-important-bg;
      }
    }
    .rest-member-wrap {
      @apply relative flex justify-center items-center;
      width: 22px;
      height: 22px;
      border: 1px solid;
      margin-right: 3px;

      &:not(.hidden-avatar) {
        border: 1px solid;
        @apply border-important-border bg-important-bg text-text-default hover:bg-important-hover;
      }

      .v-popover {
        display: flex;
      }

      .members-dropdown {
        position: absolute;
        left: 0;
        top: 42px;
      }
    }
  }
}
.add-members-wrap {
  @apply flex items-center;

  .add-members-button {
    width: fit-content;
    display: flex;
    align-items: center;
    border-radius: 50%;
    border: 1px solid;
    overflow: hidden;
    padding: 3px;
    margin: 2px 2px 2px 0px;
    @apply bg-transparent border-important-border;

    em {
      pointer-events: none;
      transition: background-color 0.2s ease-in-out;
      @apply bg-icon-default;
    }

    .small-icon {
      width: 12px;
      height: 12px;
    }
  }
}

.tooltip {
  .tooltip-arrow {
    display: none;
  }

  &.popover {
    &.members-list-popover {
      .popover-inner {
        padding: 0px;
        box-shadow: unset;
      }
    }
  }
  &.card-preview-tooltip {
    margin-top: 4px;
  }
  &.header-members-tooltip {
    .popover-inner {
      border-radius: 8px;
      display: unset !important;
    }
    .tooltip-inner {
      border-radius: 8px;
      padding: 4px 12px;
    }
  }
}

.mobile {
  .remove-owner-icon {
    @apply flex;
  }
}
</style>
